import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../AiGovernance/AiGovernance.css';
import './isms.css';
import IsmsNav from './IsmsNav';
import { getOrgPermissions } from '../../utils/permissions';
import { listRoles, assignRole, removeRole, listOrgUsers, userLabel, errMsg } from './ismsApi';

const ROLES = [
  { key: 'ISMS_OWNER', i18n: 'Isms.roleOwner', label: 'ISMS Owner (Compliance Officer)', hint: 'Accountable for the ISMS. One person.', single: true },
  { key: 'MGMT_REPRESENTATIVE', i18n: 'Isms.roleMgmt', label: 'Management Representative', hint: 'Top-management sponsor. One person.', single: true },
  { key: 'INTERNAL_AUDITOR', i18n: 'Isms.roleAuditor', label: 'Internal Auditor', hint: 'Runs the internal audit (Clause 9.2).' },
  { key: 'RISK_OWNER', i18n: 'Isms.roleRisk', label: 'Risk Owner', hint: 'Owns treatment of specific risks.' },
  { key: 'CONTROL_OWNER', i18n: 'Isms.roleControl', label: 'Control Owner', hint: 'Owns implementation of specific controls.' },
];

export default function IsmsRoles() {
  const { t } = useTranslation();
  const { canManageFrameworks } = getOrgPermissions();
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null); // {role, userId, note}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([listRoles(), listOrgUsers().catch(() => [])]);
      setRoles((r || []).filter((x) => x.active !== false));
      setUsers(u);
      setError('');
    } catch (e) {
      setError(errMsg(e, t('Isms.loadFailed', 'Could not load roles.')));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.userId) { setError(t('Isms.pickUser', 'Choose a person.')); return; }
    setSaving(true);
    try {
      await assignRole({ role: form.role, userId: Number(form.userId), note: form.note || null });
      setForm(null);
      setError('');
      await load();
    } catch (e) {
      setError(errMsg(e, t('Isms.saveFailed', 'Could not save.')));
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id) => {
    try { await removeRole(id); await load(); } catch (e) { setError(errMsg(e, t('Isms.saveFailed', 'Could not save.'))); }
  };

  const owner = roles.find((r) => r.role === 'ISMS_OWNER');

  return (
    <div className="aig-page">
      <div className="aig-header">
        <h1 className="aig-title">{t('Isms.rolesTitle', 'Roles & Owner')}</h1>
        <p className="aig-subtitle">{t('Isms.rolesSubtitle', 'ISO 27001 Clause 5.3: top management assigns and communicates ISMS responsibilities.')}</p>
      </div>
      <IsmsNav />
      {error && <div className="isms-error">{error}</div>}
      {!owner && !loading && (
        <div className="isms-notice">{t('Isms.noOwner', 'No ISMS owner has been appointed. Auditors will ask who is accountable for the ISMS.')}</div>
      )}

      {loading ? <div className="aig-spinner" /> : ROLES.map((def) => {
        const rows = roles.filter((r) => r.role === def.key);
        return (
          <div className="isms-panel" key={def.key}>
            <div className="isms-row">
              <div style={{ flex: 1 }}>
                <h4>{t(def.i18n, def.label)}</h4>
                <div className="aig-muted" style={{ fontSize: '0.75rem' }}>{def.hint}</div>
              </div>
              {canManageFrameworks && (
                <button type="button" className="aig-btn" onClick={() => setForm({ role: def.key, userId: '', note: '' })}>
                  {def.single && rows.length ? t('Isms.replace', 'Replace') : `+ ${t('Isms.assign', 'Assign')}`}
                </button>
              )}
            </div>
            {rows.length === 0 ? (
              <div className="aig-muted" style={{ fontSize: '0.8125rem', marginTop: '0.5rem' }}>{t('Isms.unassigned', 'Not assigned')}</div>
            ) : (
              <table className="aig-table" style={{ marginTop: '0.6rem' }}>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td><strong>{r.userName || r.userEmail}</strong><div className="aig-muted">{r.userEmail}</div></td>
                      <td>{r.appointedAt ? String(r.appointedAt).slice(0, 10) : ''}</td>
                      <td>{r.note}</td>
                      <td>{canManageFrameworks && <button type="button" className="isms-link isms-link-danger" onClick={() => remove(r.id)}>{t('Isms.remove', 'Remove')}</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {form && (
        <div className="aig-overlay" onClick={() => setForm(null)}>
          <div className="aig-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t((ROLES.find((r) => r.key === form.role) || {}).i18n, (ROLES.find((r) => r.key === form.role) || {}).label)}</h3>
            <div className="aig-field">
              <label>{t('Isms.person', 'Person')}</label>
              <select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })}>
                <option value="">{t('Isms.choose', 'Choose...')}</option>
                {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
              </select>
            </div>
            <div className="aig-field">
              <label>{t('Isms.note', 'Appointment note (optional)')}</label>
              <textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <div className="aig-modal-actions">
              <button type="button" className="aig-btn" onClick={() => setForm(null)}>{t('common.cancel', 'Cancel')}</button>
              <button type="button" className="aig-btn aig-btn-primary" disabled={saving} onClick={save}>{t('common.save', 'Save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
